import type { QueuedMutation } from '../queue/types';
import { isStaleWriteError, staleWriteMessage } from './staleWriteFailure';

/**
 * Word a failed mutation's TYPE for a rep, e.g. 'survey.create' -> 'survey',
 * 'survey.updateStatus' -> 'survey update status' -> humanized to
 * 'survey update status'. Unknown namespaces fall back to the raw prefix
 * with underscores replaced by spaces, so a Discard confirmation is never
 * silently blank for a mutation type this module doesn't specifically know.
 */
const TYPE_NOUNS: Record<string, string> = {
  survey: 'survey',
  form: 'form submission',
};

/** 'create' is the common case ("survey.create") and reads naturally as just
 * "This survey", not "This survey create" — every other action word (e.g.
 * 'updateStatus') is spelled out so the rep knows what kind of change it was. */
const SILENT_ACTIONS = new Set(['create']);

function humanizeCamel(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
}

function describeMutationType(type: string): string {
  const [prefix, ...rest] = type.split('.');
  const noun = TYPE_NOUNS[prefix] ?? prefix.replace(/_/g, ' ');
  const rawAction = rest.join(' ');
  if (SILENT_ACTIONS.has(rawAction)) return noun;
  const action = humanizeCamel(rawAction);
  return action ? `${noun} ${action}` : noun;
}

/**
 * Build the exact sentence a Discard confirmation shows, naming what will be
 * lost — never "0 photos" when there is real content, and never claiming
 * content (notes, contacts) that isn't actually present in the payload.
 * `photoCount` MUST already be the real, existing, non-original count (see
 * SyncEngine.getFailedMutations) — this function does no counting itself.
 *
 * Recognizes `noteCards`/`surveyContacts` array fields (the shared
 * survey.create payload shape used by both SurveyCapture and
 * CameraRollImport) when present; any other mutation type still gets an
 * honest description built from its type + real photo count alone.
 */
export function describeFailedMutation(mutation: QueuedMutation, photoCount: number): string {
  const payload = (mutation.payload && typeof mutation.payload === 'object' ? mutation.payload : {}) as Record<
    string,
    unknown
  >;
  const parts: string[] = [];
  if (Array.isArray(payload.noteCards) && payload.noteCards.length > 0) parts.push('notes');
  if (Array.isArray(payload.surveyContacts) && payload.surveyContacts.length > 0) parts.push('contacts');
  // A whole-map save (survey.saveMap) names what it holds, so a rep can tell which edit this is.
  if (Array.isArray(payload.pins) && payload.pins.length > 0) {
    parts.push(`${payload.pins.length} pin${payload.pins.length === 1 ? '' : 's'}`);
  }
  if (photoCount > 0) parts.push(`${photoCount} photo${photoCount === 1 ? '' : 's'}`);

  const noun = describeMutationType(mutation.type);
  const detail = parts.length > 0 ? ` (${parts.join(', ')})` : '';
  // A stale-write refusal names its reason: the generic sentence alone reads like a fault.
  if (isStaleWriteError(mutation.error)) {
    return `This ${noun}${detail} was not applied: ${staleWriteMessage(mutation.error as string)}`;
  }
  return `This ${noun}${detail} will not be saved.`;
}
